import { oc } from "@orpc/contract";
import { z } from "zod";

/** Ground-truth label a user assigns to a sample email. */
export const sd849SampleLabelSchema = z.enum(["is_849", "not_849", "unknown"]);

export const sampleEmailSchema = z.object({
  id: z.uuid(),
  supplierId: z.uuid().nullable(),
  inboundEmailId: z.uuid().nullable(),
  sourceRef: z.string().nullable(),
  subject: z.string().nullable(),
  fromAddress: z.string().nullable(),
  label: sd849SampleLabelSchema,
  /** Cached last dry-run coverage summary (supplier, parse counts, match/VDM status). */
  coverage: z.record(z.string(), z.unknown()).nullable(),
  coverageAt: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  /** Whether the latest coverage contradicts an `is_849` label (format drift), with reasons. */
  drift: z.object({
    drifted: z.boolean(),
    reasons: z.array(z.string()),
  }),
});

export const listSampleEmailsInputSchema = z.object({
  supplierId: z.uuid().optional(),
  label: sd849SampleLabelSchema.optional(),
  /** Return only drifted samples (filtered server-side before pagination). */
  driftedOnly: z.boolean().optional(),
  limit: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
});

export const listSampleEmailsResponseSchema = z.object({
  samples: z.array(sampleEmailSchema),
  total: z.number(),
});

export const setSampleEmailLabelInputSchema = z.object({
  id: z.uuid(),
  label: sd849SampleLabelSchema,
  notes: z.string().max(2000).optional(),
});

const listSampleEmailsContract = oc
  .input(listSampleEmailsInputSchema)
  .output(listSampleEmailsResponseSchema)
  .route({
    method: "GET",
    summary: "List labeled 849 sample emails (config-authoring library)",
  });

const setSampleEmailLabelContract = oc
  .input(setSampleEmailLabelInputSchema)
  .output(sampleEmailSchema)
  .route({
    summary: "Set the is-849 label (and optional notes) on a sample email",
  });

export const sampleEmailsContracts = {
  list: listSampleEmailsContract,
  setLabel: setSampleEmailLabelContract,
};

export type Sd849SampleLabel = z.infer<typeof sd849SampleLabelSchema>;
export type SampleEmail = z.infer<typeof sampleEmailSchema>;
export type ListSampleEmailsInput = z.infer<typeof listSampleEmailsInputSchema>;
export type ListSampleEmailsResponse = z.infer<
  typeof listSampleEmailsResponseSchema
>;
export type SetSampleEmailLabelInput = z.infer<
  typeof setSampleEmailLabelInputSchema
>;
